import React from "react";
import { Link } from "react-router-dom";
import { Mail, Globe2, AlertTriangle } from "lucide-react";
import { useAuth } from "../lib/auth";

/**
 * Subtle inline banner — under the PageHeader — telling the user which
 * company + primary PEC address the current view is scoped to.
 *
 * Rules (Task 13 + Task 14 P1 bugfix):
 *   • Super Admin in "__all__" global mode → neutral info-styled banner
 *     "Modalità globale — dati aggregati di tutte le società".
 *   • Real company with a `primary_pec_address` → gray inline reminder with
 *     the PEC address. This value is the SINGLE SOURCE OF TRUTH from
 *     `companies[].primary_pec_address` in the auth store (populated by
 *     /api/auth/me). It is NEVER derived from mailboxes — mailboxes are
 *     shown only as suggestions inside the AdminCompanies modal.
 *   • Real company WITHOUT primary_pec_address → warning-styled banner
 *     prompting the admin to configure it in Amministrazione → Aziende.
 *   • No company selected / not logged in → hidden.
 */
export default function CompanyPecBanner() {
    const { companies, companyId, isSuperAdmin, canAdmin } = useAuth();

    if (!companyId) return null;

    // Super Admin global view
    if (companyId === "__all__" && isSuperAdmin()) {
        return (
            <div
                data-testid="company-pec-banner-global"
                className="flex items-center gap-2 text-xs text-[#1E3A8A] bg-[#2563EB]/5 border border-[#2563EB]/25 rounded-lg px-3 py-2 -mt-2"
            >
                <Globe2 className="w-3.5 h-3.5 text-[#2563EB]" />
                <span>
                    <span className="font-semibold">Modalità globale</span>
                    <span className="text-[#334155]"> — dati aggregati di tutte le società. Seleziona una società dalla sidebar per operazioni mirate.</span>
                </span>
            </div>
        );
    }

    const c = (companies || []).find((x) => x.id === companyId);
    if (!c) return null;

    // Fallback: company selected but no primary_pec_address configured.
    // Do NOT derive from mailboxes — show a clear "not configured" hint.
    if (!c.primary_pec_address) {
        // Admins get a link to configure it, others just see the notice.
        const admin = canAdmin?.();
        return (
            <div
                data-testid="company-pec-banner-missing"
                className="flex items-center gap-2 text-xs text-[#92400E] bg-[#F59E0B]/10 border border-[#F59E0B]/40 rounded-lg px-3 py-2 -mt-2"
            >
                <AlertTriangle className="w-3.5 h-3.5 text-[#F59E0B]" />
                <span>
                    Società:{" "}
                    <span
                        className="font-semibold text-[#78350F]"
                        data-testid="company-pec-banner-company"
                    >
                        {c.name}
                    </span>
                    {" · "}
                    <span className="text-[#78350F]">PEC di riferimento non configurata.</span>
                </span>
                {admin && (
                    <Link
                        to="/admin/companies"
                        data-testid="company-pec-banner-configure-link"
                        className="ml-auto text-[#2563EB] hover:underline font-medium"
                    >
                        Configura
                    </Link>
                )}
            </div>
        );
    }

    return (
        <div
            data-testid="company-pec-banner"
            className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-[#64748B] -mt-2"
        >
            <div className="flex items-center gap-1.5">
                <Mail className="w-3.5 h-3.5 text-[#94A3B8]" />
                <span>
                    Società:{" "}
                    <span
                        className="font-medium text-[#172033]"
                        data-testid="company-pec-banner-company"
                    >
                        {c.name}
                    </span>
                </span>
            </div>
            <div className="flex items-center gap-1.5">
                <span className="text-[#94A3B8]">·</span>
                <span>
                    PEC di riferimento:{" "}
                    <span
                        className="font-medium text-[#334155]"
                        data-testid="company-pec-banner-value"
                    >
                        {c.primary_pec_address}
                    </span>
                </span>
            </div>
        </div>
    );
}
